import React, { useRef, useState, useEffect } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';

const REDUCED =
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Four green placeholder gradients (varied so tiles aren't identical),
// same family as PracticePrime's placeholders.
const PLACE = [
  'linear-gradient(180deg,#DEDFD6,#C2C8B6 46%,#8B9A7C 74%,#54644B)',
  'linear-gradient(180deg,#E9ECE5,#D8DFD1 45%,#AEBFA2 72%,#7E9372)',
  'linear-gradient(160deg,#D6DBCB,#AEB98F 50%,#6E7D4A 82%)',
  'linear-gradient(200deg,#E3E0D4,#C9BFB0 48%,#8B7B65 84%)',
];

const PracticeRibbon = () => {
  const { data } = usePracticeContent();
  const images = (data && data.images) || {};

  const ref = useRef(null);
  const [inView, setInView] = useState(!!REDUCED);
  useEffect(() => {
    if (REDUCED) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const tiles = [1, 2, 3, 4].map((n) => images[`ribbon_${n}`] || { url: '', caption: '' });

  return (
    <section ref={ref} style={{ background: '#F8F8F7', padding: '96px 0' }} data-testid="practice-ribbon-section">
      <style>{`
        .rib-rev{ opacity:0; transform:translateY(18px); transition:opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .rib-rev.in{ opacity:1; transform:none; }
        .rib-rule{ position:relative; height:1px; background:rgba(58,53,48,.18); }
        .rib-rule-wrap{ position:relative; }
        .rib-eyebrow{ position:relative; display:inline-block; background:#F8F8F7; padding-right:18px;
          font-family:Inter, sans-serif; font-weight:600; font-size:11.5px; letter-spacing:.3em;
          color:#4A6A9C; text-transform:lowercase; transform:translateY(-50%); }
        .rib-track-wrap{ overflow:hidden; margin-top:6px; }
        .rib-grid{ display:grid; grid-template-columns:repeat(4,1fr); gap:2px; width:112%;
          animation:ribDrift 24s ease-in-out infinite alternate; will-change:transform; }
        @keyframes ribDrift{ from{ transform:translateX(0); } to{ transform:translateX(-8%); } }
        .rib-track-wrap:hover .rib-grid{ animation-play-state:paused; }
        .rib-media{ position:absolute; inset:0; background-size:cover; background-position:center;
          transition:transform .45s cubic-bezier(.2,.8,.2,1); }
        .rib-tile{ transition:opacity .4s ease; }
        .rib-grid:hover .rib-tile{ opacity:.6; }
        .rib-grid .rib-tile:hover{ opacity:1; }
        .rib-grid .rib-tile:hover .rib-media{ transform:scale(1.04); }
        .rib-tile .rib-cap{ opacity:0; transition:opacity .3s ease; }
        .rib-tile:hover .rib-cap{ opacity:1; }
        .rib-below{ margin-top:2px; }
        @media (max-width:880px){ .rib-grid{ grid-template-columns:1fr 1fr; width:100%; animation:none; will-change:auto; } }
        @media (prefers-reduced-motion: reduce){ .rib-rev{ opacity:1; transform:none; transition:none; }
          .rib-grid{ animation:none; will-change:auto; } }
      `}</style>

      {/* hairline rule with the eyebrow sitting on it */}
      <div className={`rib-rule-wrap rib-rev${inView ? ' in' : ''}`}>
        <div className="rib-rule" />
        <div className="pr-spine">
          <span className="rib-eyebrow" data-testid="practice-ribbon-eyebrow">a few of the thousand</span>
        </div>
      </div>

      <div className={`rib-track-wrap rib-rev${inView ? ' in' : ''}`} data-testid="practice-ribbon-track">
        <div className="rib-grid" data-testid="practice-ribbon-grid">
        {tiles.map((t, i) => (
          <div key={i} className="rib-tile"
            style={{ position: 'relative', height: 'clamp(300px,26vw,380px)', overflow: 'hidden', background: t.url ? '#000' : '#E9ECE5' }}
            data-testid={`practice-ribbon-tile-${i + 1}`}>
            {t.url ? (
              <img className="rib-media" src={t.url} alt={t.caption || 'project'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div className="rib-media" style={{ background: PLACE[i] }} />
            )}
            {t.caption && (
              <span className="rib-cap"
                style={{ position: 'absolute', left: 16, bottom: 14, fontFamily: 'Inter, sans-serif', fontSize: '10.5px', letterSpacing: '0.14em', color: '#F8F8F7', textShadow: '0 1px 8px rgba(30,40,28,.45)' }}>
                {t.caption}
              </span>
            )}
          </div>
          ))}
        </div>
      </div>

      <div className={`rib-rule rib-below rib-rev${inView ? ' in' : ''}`} data-testid="practice-ribbon-rule-below" />
    </section>
  );
};

export default PracticeRibbon;
